import Big from 'big.js'
import moment from 'moment'
import { findTag } from '../helpers'

/**
 * @typedef {import('../../api-v2/js-doc').V1CreateBrokerPaymentRequest} V1CreateBrokerPaymentRequest
 * @typedef {import('../../api-v2/js-doc').V1BrokerPaymentResponse} V1BrokerPaymentResponse
 * @typedef {import('../../api-v2/js-doc').V2CreateBrokerPaymentRequest} V2CreateBrokerPaymentRequest
 * @typedef {import('../../api-v2/js-doc').V2BrokerPaymentResponse} V2BrokerPaymentResponse
 * @typedef {import('../../api-v2/js-doc').V1BrokerPaymentTransactionType} V1BrokerPaymentTransactionType
 * @typedef {import('../../api-v2/js-doc').V1UpdateBrokerPaymentRequest} V1UpdateBrokerPaymentRequest
 * @typedef {import('../../api-v2/js-doc').V2UpdateBrokerPaymentRequest} V2UpdateBrokerPaymentRequest
 * @typedef {import('../../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 */

/**
 * Uses V1 request body to create the V2 request body.
 * 
 * If the V1 response body is present, it appends the ID to the V2 request body.
 * 
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1CreateBrokerPaymentRequest} v1Request - V1 create invoice request body
 * @param {V1BrokerPaymentResponse=} v1Response - V1 create invoice response body
 * @returns {V2CreateBrokerPaymentRequest}
 */

export const mapCreateBrokerPaymentV1RequestToV2Request = (v2MappingOptions, v1Request, v1Response) => {
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    amount: v1Request.amount,
    batchDate: moment(v1Request.batch_date).tz('America/New_York').toDate(),
    checkNumber: v1Request.check_number ?? undefined,
    id: v1Response?.id,
    invoiceId: v1Request.invoice_id,
    type: v1Request.transaction_type
      ? v1Request.transaction_type === 'lockbox'
        ? 'Check'
        : 'ACH'
      : undefined,
    tag: new Big(v1Request.amount).eq(0)
      ? {
        key: findTag(v1Request.update_status) || 'OTHER',
        note: '',
      }
      : undefined,
  }
}

/**
 * Uses V1 request body to create the V2 request body.
 * 
 * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
 * @param {V1UpdateBrokerPaymentRequest} v1Request - V1 create invoice request body
 * @returns {V2UpdateBrokerPaymentRequest}
 */

export const mapUpdateBrokerPaymentV1RequestToV2Request = (v2MappingOptions, v1Request) => {
  return {
    __v1Payload: v1Request,
    __ingestThrough: v2MappingOptions.ingestThrough,
    batchDate: moment(v1Request.batch_date).tz('America/New_York').toDate(),
    checkNumber: v1Request.check_number ?? undefined,
    type:  v1Request.transaction_type === 'lockbox'
      ? 'Check'
      : 'ACH',
  }
}

/**
 * @param {V2BrokerPaymentResponse[]} v2ItemsResponse - V2 broker payments request body
 * @returns {V1BrokerPaymentResponse[]}
 */
export const mapBrokerPaymentsV2ResponseToV1Response = (v2ItemsResponse) => {
  return v2ItemsResponse.map((item, index) => {
    return mapBrokerPaymentV2ResponseToV1Response(item, index + 1) 
  })
}

/**
 * @param {V2BrokerPaymentResponse} v2Response - V2 broker payment request body
 * @param {number} paymentNumber - payment number assigned to the broker payment number 
 * @returns {V1BrokerPaymentResponse}
 */
export const mapBrokerPaymentV2ResponseToV1Response = (v2Response, paymentNumber = 1) => {
  return {
    amount: new Big(v2Response.amount).toNumber(),
    batch_date: v2Response.batchDate,
    changelog_notes: null, // null on responses from V1
    check_number: v2Response.checkNumber,
    created_at: v2Response.createdAt,
    days_to_pay: null, // null on responses from V1
    debtor_id: '', // not available from V2, but not used in the UI
    firebase_key: null, // null on responses from V1
    metadata: {}, // not available from V2, but not used in the UI,
    id: v2Response.id, 
    invoice_id: v2Response.invoiceId,
    payment_number: paymentNumber,
    transaction_type: mapBrokerPaymentV2TypeToV1Type(v2Response),
    updated_at: v2Response.updatedAt,
    updated_by: v2Response.updatedBy,
  }
}

/**
 * @param {V2BrokerPaymentResponse} brokerPayment - V2 broker payment response
 * @returns {V1BrokerPaymentTransactionType=}
 */
const mapBrokerPaymentV2TypeToV1Type = (brokerPayment) => {
  if (brokerPayment.type === 'ACH') {
    return 'ach'
  }
  if (brokerPayment.type === 'Check' ) {
    return 'lockbox'
  }
  return 'lockbox'
}
